import { useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { rotuloDeNivel } from '@orc/shared';
import { useAuth } from '../auth/AuthContext.js';
import { Carregando, Erro, Vazio } from '../componentes/Estados.js';
import { SeletorPeriodo } from '../componentes/SeletorPeriodo.js';
import { moedaBr, rotuloPagamento, rotuloTabela } from '../lib/formato.js';
import { mensagemDoErro, requisitar } from '../lib/api.js';
import { periodoIncompleto, periodoInvertido, ultimosDias, type Periodo } from '../lib/periodo.js';
import {
  baseDoModo,
  buscarNiveisDelCredere,
  rotuloDoModo,
  segmentoDoModo,
  TABELA_DEL_CREDERE,
  type ModoGerador,
} from '../estado/modo.js';
import './Orcamento.css';

interface ResumoOrcamento {
  id: string;
  numero: string | null;
  status: string;
  clienteNome: string;
  tabela: string;
  paymentType: string;
  criadoEm: string;
  valorTotal: string | null;
  /** Nível padrão — só vem preenchido no quadrante del credere. */
  delNivelPadrao?: string | null;
  /**
   * Quem montou o orçamento (Story F.2).
   *
   * Serve a uma decisão só: mostrar ou não o botão de excluir. O vendedor apaga
   * apenas o próprio rascunho — a guarda de verdade é o 403 do servidor, e isto
   * evita oferecer um botão que sempre falharia.
   */
  vendedorId: string;
}

const ROTULO_STATUS: Record<string, { texto: string; classe: string }> = {
  rascunho: { texto: 'Rascunho', classe: 'etiqueta' },
  emitido: { texto: 'Emitido', classe: 'etiqueta etiqueta--ok' },
  enviado: { texto: 'Enviado', classe: 'etiqueta etiqueta--acao' },
  expirado: { texto: 'Expirado', classe: 'etiqueta etiqueta--aviso' },
};

function dataBr(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Lista de orçamentos — Story 8.3, com o modo do Épico 9.
 *
 * A MESMA tela serve às duas seções. O que o modo muda é o recorte e os
 * textos: `/orcamentos` esconde os del credere, `/del-credere` mostra só
 * eles. Sem esse recorte, a "opção à parte" que o cliente pediu apareceria
 * misturada nas duas listas, e a separação seria só de menu.
 */
export function Orcamentos({ modo = 'comum' }: { modo?: ModoGerador }): JSX.Element {
  const navegar = useNavigate();
  const { usuario } = useAuth();
  const cliente = useQueryClient();
  const delCredere = modo === 'del_credere';
  const base = baseDoModo(modo);

  /*
   * Padrão: os últimos 30 dias.
   *
   * "Tudo" foi descartado, e não por gosto: a lista já tem 21 orçamentos com
   * poucas semanas de uso real e cresce toda semana. Uma lista sem recorte fica
   * lenta e, pior, deixa de responder à pergunta que se faz ao abrir a tela
   * ("o que está em aberto agora"), que é sobre o trabalho recente.
   *
   * O risco do padrão é esconder o que ficou para trás — um rascunho de 45 dias
   * some sem aviso. Duas coisas respondem a isso: o atalho ativo fica destacado
   * (a tela DIZ que está filtrando), e o estado vazio manda ampliar o período
   * em vez de sugerir que não existe nada.
   */
  const [periodo, setPeriodo] = useState<Periodo>(() => ultimosDias(new Date(), 30));

  const incompleto = periodoIncompleto(periodo);
  const invertido = periodoInvertido(periodo);
  /* Data pela metade não vai para a API: a F.1 recusa com 400, e o usuário
     veria um erro no meio de uma digitação que ainda não terminou. */
  const podeBuscar = !incompleto && !invertido;

  /** Quem pode apagar aquele rascunho — espelha a regra da F.1, sem ser a guarda. */
  const podeExcluir = (o: ResumoOrcamento): boolean => {
    if (o.status !== 'rascunho') return false;
    if (usuario === undefined) return false;
    // admin e gestor têm `excluir_rascunho_alheio`; o vendedor, só o próprio.
    return usuario.role !== 'vendedor' || o.vendedorId === usuario.id;
  };

  /*
   * Os rótulos dos níveis — Parte C da Story 2 (Frente 3).
   *
   * A lista trazia `del_3` cru, que é chave interna e não diz nada a quem lê:
   * o vendedor quer ver "7% · DEL 5%". O rótulo NÃO é escrito aqui — vem do
   * mesmo endpoint que a tela de criação usa, porque o pareamento tem uma
   * definição só, no backend. Duas listas divergiriam, e o sintoma seria a
   * etiqueta errada sobre o preço certo (o defeito que a correção de 28/08
   * consertou).
   *
   * `enabled` restringe a busca ao quadrante del credere: nas outras tabelas a
   * coluna nem mostra nível.
   */
  const niveis = useQuery({
    queryKey: ['niveis-del-credere'],
    // Envolvido: o TanStack passa o CONTEXTO como 1o argumento, e desde a S4
    // a funcao aceita a tabela ali — sem o wrapper ela receberia o contexto.
    queryFn: async () => buscarNiveisDelCredere(),
    enabled: delCredere,
    staleTime: Infinity,
  });

  /**
   * `del_3` → `"7% · DEL 5%"`, `com_4_5` → `"4,5%"`.
   *
   * A lista do servidor primeiro; `rotuloDeNivel` como rede. A rede importa
   * desde a S4: a consulta só roda no del credere (`enabled: delCredere`), e um
   * orçamento CPF com comissão escolhida cairia no `?? nivel` mostrando
   * `com_4_5` cru na listagem.
   */
  const rotuloNivel = (nivel: string | null | undefined): string => {
    if (nivel === null || nivel === undefined) return '—';
    return niveis.data?.find((n) => n.nivel === nivel)?.rotulo ?? rotuloDeNivel(nivel) ?? nivel;
  };

  const lista = useQuery({
    /*
     * O período FAZ PARTE da chave. Sem ele, trocar o filtro devolveria o
     * cache do período anterior — uma lista velha sob um recorte novo.
     */
    queryKey: ['orcamentos', modo, periodo.de, periodo.ate],
    queryFn: async ({ signal }) => {
      /*
       * `URLSearchParams` monta a query, e os nomes são exatamente `de` e
       * `ate` — os mesmos do dashboard, e os únicos que a F.1 aceita.
       */
      const q = new URLSearchParams({ de: periodo.de, ate: periodo.ate });
      /*
       * Cada seção vê só a sua — S5.
       *
       * O del credere se recorta por QUADRANTE; vinílico e rodapé, por
       * SEGMENTO. A comum é a ausência dos dois: sem `semSegmento` os pedidos
       * das seções novas apareceriam nela, e a separação seria só de menu —
       * exatamente o que o cliente pediu para não acontecer.
       */
      const segmento = segmentoDoModo(modo);
      if (delCredere) q.set('tabela', TABELA_DEL_CREDERE);
      else if (segmento !== null) q.set('segmento', segmento);
      else {
        q.set('naoDelCredere', '1');
        q.set('semSegmento', '1');
      }
      return (
        await requisitar<ResumoOrcamento[]>(`/api/orcamentos?${q.toString()}`, { sinal: signal })
      ).dados;
    },
    enabled: podeBuscar,
    /*
     * Sempre fresca ao voltar para cá.
     *
     * O `staleTime` global de 30s é bom para catálogo, que muda por importação;
     * aqui é o contrário: o usuário chega nesta tela logo depois de criar,
     * emitir ou duplicar, e via a lista velha — o orçamento que ele acabou de
     * emitir aparecia como rascunho, ou nem aparecia.
     */
    staleTime: 0,
    refetchOnMount: 'always',
  });

  /*
   * Excluir rascunho — Story F.2, consumindo o DELETE da F.1.
   *
   * A confirmação é do navegador (`confirm`), e é uma escolha consciente: o
   * projeto não tem componente de diálogo, e inventar um modal aqui seria uma
   * peça de UI nova numa story de lista. O texto diz o que não pode ser
   * desfeito, que é a informação que importa antes de um clique irreversível.
   */
  const exclusao = useMutation({
    mutationFn: async (id: string) => {
      await requisitar(`/api/orcamentos/${id}`, { metodo: 'DELETE' });
      return id;
    },
    onSuccess: () => {
      /*
       * Invalida em vez de remover a linha na mão: o servidor é quem sabe o
       * que sobrou, e a contagem do período muda junto. Mexer só no cache
       * local deixaria `meta.total` mentindo até a próxima visita.
       */
      void cliente.invalidateQueries({ queryKey: ['orcamentos'] });
    },
  });

  return (
    <section className="pagina">
      <header className="pagina__cabecalho">
        <div>
          {/* O título é o da SEÇÃO — o mesmo texto do menu, para quem chega
              pelo link saber onde está. */}
          <h1>{modo === 'comum' ? 'Orçamentos' : `Orçamentos ${rotuloDoModo(modo)}`}</h1>
          <p className="pagina__sub">
            {delCredere
              ? 'Tabela del credere, com escolha da coluna de comissão por item.'
              : 'Rascunhos podem ser editados; emitidos ficam congelados e podem ser duplicados.'}
          </p>
        </div>
        <button
          type="button"
          className="botao botao--primario"
          onClick={() => {
            navegar(`${base}/novo`);
          }}
        >
          {delCredere ? 'Novo del credere' : 'Novo orçamento'}
        </button>
      </header>

      <div className="cartao lista__filtro">
        <SeletorPeriodo periodo={periodo} aoMudar={setPeriodo} />
      </div>

      {/*
        Falha ao EXCLUIR não é falha de carregamento: vai num aviso inline, e
        não no <Erro>, cujo título fixo ("Não deu para carregar") diria a coisa
        errada. O 409 tem texto próprio — ele significa que o rascunho virou
        emitido entre a lista e o clique.
      */}
      {exclusao.isError && (
        <p className="aviso" role="alert">
          {mensagemDoErro(exclusao.error)}
        </p>
      )}

      {!podeBuscar ? (
        /* O SeletorPeriodo já mostra o porquê, junto dos campos. Aqui só não se
           finge que a lista está carregando. */
        <div className="cartao">
          <Vazio
            titulo="Ajuste o período"
            descricao="Escolha um intervalo válido para ver os orçamentos."
          />
        </div>
      ) : lista.isPending ? (
        <div className="cartao">
          <Carregando rotulo="Carregando orçamentos…" />
        </div>
      ) : lista.isError ? (
        <div className="cartao">
          <Erro
            erro={lista.error}
            aoRepetir={() => {
              void lista.refetch();
            }}
          />
        </div>
      ) : lista.data.length === 0 ? (
        <div className="cartao">
          <Vazio
            /*
             * O texto muda conforme o filtro: com período em vigor, "nenhum
             * orçamento ainda" seria mentira — pode haver dezenas, fora do
             * recorte. Dizer o período e sugerir ampliá-lo é o que resolve.
             */
            titulo={
              delCredere
                ? 'Nenhum orçamento del credere neste período'
                : 'Nenhum orçamento neste período'
            }
            descricao={`Nada foi criado entre ${periodo.de} e ${periodo.ate}. Amplie o período acima, ou crie o primeiro.`}
            acao={
              <button
                type="button"
                className="botao botao--primario"
                onClick={() => {
                  navegar(`${base}/novo`);
                }}
              >
                {delCredere ? 'Novo del credere' : 'Novo orçamento'}
              </button>
            }
          />
        </div>
      ) : (
        <div className="cartao tabela-rolagem">
          <table className="tabela tabela--cards">
            <thead>
              <tr>
                <th scope="col">Número</th>
                <th scope="col">Cliente</th>
                <th scope="col">{delCredere ? 'Nível' : 'Tabela'}</th>
                <th scope="col">Pagamento</th>
                <th scope="col">Criado em</th>
                <th scope="col" className="num">
                  Valor total
                </th>
                <th scope="col">Situação</th>
                {/* Sem rótulo visível: a coluna só tem botão, e "Ações" seria
                    ruído numa tabela que já é larga. O nome acessível está no
                    aria-label de cada botão, que diz de qual orçamento é. */}
                <th scope="col">
                  <span className="visualmente-oculto">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {lista.data.map((o) => {
                const status = ROTULO_STATUS[o.status] ?? { texto: o.status, classe: 'etiqueta' };
                return (
                  <tr key={o.id}>
                    <td data-rotulo="Número">
                      <Link className="link" to={`${base}/${o.id}`}>
                        {o.numero ?? 'sem número'}
                      </Link>
                    </td>
                    <td data-rotulo="Cliente">{o.clienteNome}</td>
                    <td data-rotulo={delCredere ? 'Nível' : 'Tabela'}>
                      {delCredere ? rotuloNivel(o.delNivelPadrao) : rotuloTabela(o.tabela)}
                    </td>
                    <td data-rotulo="Pagamento">{rotuloPagamento(o.paymentType)}</td>
                    <td data-rotulo="Criado em">{dataBr(o.criadoEm)}</td>
                    <td className="num" data-rotulo="Valor total" data-destaque="sim">
                      {/* Rascunho não tem valor congelado: mostrar um número
                          aqui daria a entender que o preço já está fechado. */}
                      {o.valorTotal === null ? (
                        <span className="tenue">em aberto</span>
                      ) : (
                        moedaBr(o.valorTotal)
                      )}
                    </td>
                    <td data-rotulo="Situação">
                      <span className={status.classe}>{status.texto}</span>
                    </td>
                    <td className="lista__acoes" data-rotulo="">
                      {/*
                        O botão só existe em rascunho de quem pode apagá-lo.
                        Emitido NUNCA tem botão: o servidor recusa com 409, e
                        oferecer um caminho que sempre falha é pior do que não
                        oferecer nada.
                      */}
                      {podeExcluir(o) && (
                        <button
                          type="button"
                          className="botao botao--discreto lista__excluir"
                          aria-label={`Excluir rascunho ${o.numero ?? 'sem número'} de ${o.clienteNome}`}
                          disabled={exclusao.isPending}
                          onClick={() => {
                            if (!window.confirm('Excluir este rascunho? Não pode ser desfeito.')) {
                              return;
                            }
                            exclusao.mutate(o.id);
                          }}
                        >
                          Excluir
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
